/**
 * CL-384A / CL-409D — Project journey timeline model.
 * Client portal uses admin-controlled stages when projectJourney is set; otherwise a neutral default.
 */

import { buildAdminControlledProjectJourneyModel } from "./projectJourneyAdminModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

function normalizeKey(value) {
  return safeStr(value).toLowerCase().replace(/[\s-]+/g, "_");
}

export const JOURNEY_MILESTONE_STATE = {
  COMPLETED: "completed",
  CURRENT: "current",
  UPCOMING: "upcoming",
};

/** Ordered milestones — index drives completed / current / upcoming. */
export const PROJECT_JOURNEY_MILESTONE_DEFS = [
  {
    key: "discovery",
    label: "Discovery",
    stageTitle: "Discovery",
    description: "Understanding your goals, site, and project scope.",
    tooltipExplanation: "Early planning to align on vision, budget, and feasibility.",
  },
  {
    key: "concept_design",
    label: "Concept Design",
    stageTitle: "Concept Design",
    description: "Exploring design direction and early concepts for your project.",
    tooltipExplanation: "Concept sketches and options for your feedback.",
  },
  {
    key: "development_permit",
    label: "Development Permit",
    stageTitle: "Development Permit Drawings",
    description: "We are preparing the drawings required for Development Permit submission.",
    tooltipExplanation: "Drawings and materials for Development Permit application.",
  },
  {
    key: "client_review",
    label: "Client Review",
    stageTitle: "Client Review",
    description: "Drawings or documents are shared for your review and decision.",
    tooltipExplanation: "Your feedback, approval, or requested changes on shared files.",
  },
  {
    key: "permit_submission",
    label: "Permit Submission",
    stageTitle: "Permit Submission",
    description: "Application packages are prepared and submitted to the city.",
    tooltipExplanation: "Drawings are submitted to the City for review.",
  },
  {
    key: "city_review",
    label: "City Review",
    stageTitle: "City Review",
    description: "The city is reviewing the submitted permit application.",
    tooltipExplanation: "City staff review comments, conditions, and revisions.",
  },
  {
    key: "permit_approval",
    label: "Permit Approval",
    stageTitle: "Permit Approval",
    description: "Permits are approved or moving toward final issuance.",
    tooltipExplanation: "Permit approval and any conditions before construction drawings.",
  },
  {
    key: "construction_drawings",
    label: "Construction Drawings",
    stageTitle: "Construction Drawings",
    description: "Detailed construction drawings are prepared for building.",
    tooltipExplanation: "Issued-for-construction drawings and coordination.",
  },
  {
    key: "construction",
    label: "Construction",
    stageTitle: "Construction",
    description: "Construction phase coordination and site-related deliverables.",
    tooltipExplanation: "Active construction support and documentation.",
  },
  {
    key: "project_complete",
    label: "Project Complete",
    stageTitle: "Project Complete",
    description: "Project work is complete or in closeout.",
    tooltipExplanation: "Final deliverables and project wrap-up.",
  },
];

function normalizePlanningWorkflowStage(raw) {
  const s = normalizeKey(raw);
  const aliases = {
    planning_drawings: "drawings",
    permit_drawings: "permits",
    construction_drawings: "construction_drawings",
  };
  return aliases[s] || s || "";
}

/**
 * Resolve current milestone index from existing project fields (no invented workflow).
 * @param {object} project
 * @param {{ pendingReviewCount?: number }} context
 * @returns {number}
 */
export function resolveProjectJourneyMilestoneIndex(project, context = {}) {
  const stage = normalizeKey(project?.stage);
  const workflow = normalizePlanningWorkflowStage(project?.planningWorkflowStage);
  const gates = project?.planningGates && typeof project.planningGates === "object" ? project.planningGates : {};
  const pending = Math.max(0, Number(context.pendingReviewCount) || 0);

  if (stage === "closeout" || stage === "complete") return 9;
  if (stage === "construction") return 8;
  if (stage === "precon") return 7;
  if (workflow === "construction") return 8;
  if (workflow === "construction_drawings") return 7;

  if (gates.bpApproved) return 7;
  if (gates.dpApproved) return 6;
  if (gates.bpSubmitted && !gates.bpApproved) return 6;
  if (gates.dpSubmitted && !gates.dpApproved) return 5;

  if (stage === "permitting" || workflow === "permits") {
    if (pending > 0) return 3;
    if (gates.drawingsApproved || gates.dpSubmitted) return 4;
    return 4;
  }

  if (pending > 0) return 3;

  if (gates.drawingsApproved || workflow === "drawings") return 2;
  if (gates.conceptApproved) return 1;
  if (workflow === "concept") return 1;
  if (stage === "design") return 1;
  if (stage === "planning") return 2;

  return 0;
}

function statusLabelForState(state) {
  if (state === JOURNEY_MILESTONE_STATE.COMPLETED) return "Completed";
  if (state === JOURNEY_MILESTONE_STATE.CURRENT) return "In progress";
  return "Upcoming";
}

/** Derived progress (not persisted). Current milestone counts as half-step. */
export function computeJourneyProgressPercent(currentIndex, total = PROJECT_JOURNEY_MILESTONE_DEFS.length) {
  const n = Math.max(1, Number(total) || 1);
  const idx = Math.max(0, Math.min(Number(currentIndex) || 0, n - 1));
  if (idx >= n - 1) return 100;
  const weighted = idx + 0.5;
  return Math.min(100, Math.max(0, Math.round((weighted / n) * 100)));
}

function formatWaitingOn(pendingReviewCount) {
  const n = Math.max(0, Number(pendingReviewCount) || 0);
  if (n === 0) return null;
  if (n === 1) return "Your review of 1 drawing";
  return `Your review of ${n} drawings`;
}

function isLikelyImageFile(fileMeta) {
  const f = fileMeta || {};
  const t = safeStr(f.contentType || f.type).toLowerCase();
  if (t.startsWith("image/")) return true;
  const name = safeStr(f.name).toLowerCase();
  return /\.(png|jpg|jpeg|webp|gif|heic)$/i.test(name);
}

function filePreviewUrl(fileMeta) {
  const f = fileMeta || {};
  return safeStr(f.thumbnailUrl || f.thumbUrl || f.previewUrl || f.url) || null;
}

/** Optional visual from existing project/file fields only. */
export function resolveProjectJourneyThumbnailUrl(project, files = []) {
  const fromProject = [
    project?.coverImageUrl,
    project?.heroImageUrl,
    project?.thumbnailUrl,
    project?.imageUrl,
  ]
    .map((v) => safeStr(v))
    .find(Boolean);
  if (fromProject) return fromProject;

  const list = Array.isArray(files) ? files : [];
  const designFiles = list.filter((f) => {
    const cat = normalizeKey(f?.category);
    const sub = normalizeKey(f?.subcategory);
    return cat === "design" || sub === "renderings";
  });

  const rendering = designFiles.find((f) => normalizeKey(f?.subcategory) === "renderings" && isLikelyImageFile(f));
  if (rendering) return filePreviewUrl(rendering);

  const anyImage = designFiles.find((f) => isLikelyImageFile(f));
  return anyImage ? filePreviewUrl(anyImage) : null;
}

/**
 * @param {object} project
 * @param {{ pendingReviewCount?: number, projectName?: string, firstPendingFileId?: string|null, thumbnailUrl?: string|null, projectFiles?: object[] }} context
 */
export function buildProjectJourneyModel(project, context = {}) {
  if (!project) return null;
  return buildAdminControlledProjectJourneyModel(project, context);
}

/**
 * Pick one active project to feature on multi-project portal home.
 * @param {Array<{ project: object, files?: object[], actionSummary?: object }>} activeSummaries
 */
export function pickFocusProjectSummary(activeSummaries = []) {
  const list = Array.isArray(activeSummaries) ? activeSummaries : [];
  if (!list.length) return null;
  return list.find((s) => s?.actionSummary?.state === "needs_attention") || list[0];
}
